import each from 'lodash/each';
import merge from 'lodash/merge';
import get from 'lodash/get';
import '../../loader-base/src/polyfills/index';
import ecReady from './ec-ready';
import loadPolyfills from './load-polyfills';
import loadLibraries from './load-libraries';
import { initializeLazyBundleLoader, registerPaths } from './lazy-loader';
import { bundlesReady, getValidBundles, handleClientSpecificBundle, setBundleChunksPath } from './bundle-loader';
import setDefaultValuesEc from './set-default-values-ec';
import { componentLoader, getConfiguration, mdsCssReady, mdsIconsReady } from '../../loader-base/src/services';
import { getAppContext, loadFiles, newRelicUsageTracking } from '../../loader-base/src/helpers';
import { version } from '../package.json';

let lazyBundleDownloading = false;
let savedLoaderConfig;
let manifest = {};
const contextInfo = {};

/**
 * Add client custom stylesheets
 * @param {*} css
 */
function loadCustomStyles(css) {
    return loadFiles(css.customStyles)
        .then(() => Promise.resolve());
}

/**
 * Check if Morningstar usage tracking using new relic should be enabled
 * If it is then install the tracking agent script in the page
 * @param {object} loaderConfig - the loader config
 */
function checkAndInitializeMorningstarUsageTracking(loaderConfig) {
    if (loaderConfig.morningstarUsageTracking && loaderConfig.morningstarUsageTracking.enabled) {
        newRelicUsageTracking.installTrackingAgent(loaderConfig.morningstarUsageTracking.applicationId, () => {
            newRelicUsageTracking.setCustomAttributes(getAppContext(loaderConfig));
        });

        try {
            loaderConfig.usageTracking = loaderConfig.usageTracking || [];
            loaderConfig.usageTracking.push({
                provider: 'newRelic',
            });
        } catch (e) {
            console.error(e);
        }
        if (loaderConfig.usageTracking.length >= 2) {
            console.warn('Usage tracking is enabled for 2 or more providers. This can degrade the performance of the web page.');
        }
    }
}

/**
 * Run the component loader, downloading and installing files based on the loader config passed in.
 * @param {object} loaderConfig - the loader config
 */
function load(loaderConfig = {}) {
    setDefaultValuesEc(loaderConfig);

    contextInfo.runtime = merge({}, loaderConfig.runtime);
    contextInfo.apiTokens = merge({}, loaderConfig.apiTokens);
    contextInfo.instid = loaderConfig.component.source.instid;
    contextInfo.apiTokenExpiredCallback = loaderConfig.api.apiTokenExpiredCallback;

    let promiseChain = Promise.resolve();

    // NewRelic agent needs to be installed in <head> section
    // this should be done in the sync code and not wait for download of config or bundle
    checkAndInitializeMorningstarUsageTracking(loaderConfig);

    // Get configuration...
    if (loaderConfig.configuration.source.type === 'config-api') {
        promiseChain = promiseChain
            .then((newApiTokens) => {
                loaderConfig.apiTokens = merge(loaderConfig.apiTokens, newApiTokens);
            });
    }
    promiseChain = promiseChain.then(() => getConfiguration(loaderConfig.configuration, loaderConfig.apiTokens.apiGatewayToken));

    // ...and set configuration
    promiseChain = promiseChain.then((config) => {
        loaderConfig.config = config;
        return true;
    });

    // Download MDS icons/style if set in config
    promiseChain = promiseChain
        .then(() => mdsIconsReady(loaderConfig.css.mds, loaderConfig.component.source.environment))
        .then(() => mdsCssReady(loaderConfig.css.mds, loaderConfig.component.source.environment));

    // Download polyfills
    if (loaderConfig.polyfills) {
        promiseChain = promiseChain
            .then(() => loadPolyfills(loaderConfig.polyfills));
    }

    // If configured downloads the mwc-application-helper
    promiseChain = promiseChain
        .then(() => componentLoader.mwcReady(loaderConfig));

    // Download extra libraries
    if (loaderConfig.libraries) {
        promiseChain = promiseChain
            .then(() => loadLibraries(loaderConfig.libraries));
    }

    // Download the bundles
    promiseChain = promiseChain
        .then(() => handleClientSpecificBundle(loaderConfig))
        .then(() => getValidBundles(loaderConfig))
        .then((validBundles) => {
            loaderConfig.validBundles = validBundles;
            return Promise.resolve();
        })
        .then(() => setBundleChunksPath(loaderConfig))
        .then(() => bundlesReady(loaderConfig, componentLoader.getLoadedFiles()))
        .then((manifestContent) => {
            manifest = manifestContent;
            return Promise.resolve()
                .then(() => ecReady(loaderConfig.config, loaderConfig))
                .then(() => componentLoader.bootstrap(loaderConfig));
        });

    if (loaderConfig.lazy.bundlesToDownload) {
        lazyBundleDownloading = true;
        promiseChain = promiseChain.then(() => initializeLazyBundleLoader(loaderConfig.config));
    }

    // Load custom styles
    promiseChain = promiseChain.then(() => {
        savedLoaderConfig = loaderConfig;
        return loadCustomStyles(loaderConfig.css);
    });

    manifest.loaderConfig = loaderConfig;
    promiseChain = promiseChain.then(() => Promise.resolve(manifest));
    return promiseChain;
}

/**
 * Reset internal component loader options, e.g. resolutions and dependencies
 */
function reset() {
    componentLoader.resetInternal();
}

/**
 * Create a component element and attach to a container div
 * @param {*} componentTagName - the element tag name
 * @param {*} componentId - the element's mstarComponentId
 * @param {*} containerId - the container element id
 * @returns the container element
 */
function createAndReturnTag(componentTagName, componentId, containerId) {
    // Create component tag...
    const componentTag = document.createElement(componentTagName);
    componentTag.setAttribute('mstar-component-id', componentId);
    // ... and attach it to the container.
    const container = document.createElement('div');
    container.setAttribute('id', containerId);
    container.appendChild(componentTag);
    return container;
}

/**
 * Register instance for the specified component
 * @param {*} componentId - the component id to register
 */
function registerAndResetAsterix(componentId) {
    // Register default config for component types that need it.
    each(window.morningstar.components._defaultConfig, (defConfig, typeName) => {
        if (!window.morningstar.asterix.config.getDefaultConfig(typeName)) {
            window.morningstar.asterix.config.addType(typeName, defConfig);
        }
    });
    // Register component paths in Asterix.
    registerPaths(componentId);
    // Handle Asterix reset/init.
    const path = window.morningstar.asterix.instanceRegistry.find(componentId).modulePath;
    window.morningstar.asterix.instanceRegistry.init(path);
}

/**
 * Create a markets component and add to a container element
 * @param {*} containerParent - parent element for the container
 * @param {*} containerId - the element id for the container element
 * @param {*} componentTagName - the component tag
 * @param {*} componentId - the component id
 * @returns component instance
 */
function loadMarketsComponent(containerParent, containerId, componentTagName, componentId) {
    const container = document.createElement('div');
    container.setAttribute('id', containerId);
    container.style.height = '100%';
    containerParent.appendChild(container);

    const marketsComponentSettings = get(savedLoaderConfig, `config.components[${componentId}].settings`, {});

    return new Promise((resolve) => {
        const instance = window.morningstar.components[componentTagName].createComponent({
            element: container,
            initialConfiguration: {
                component: {
                    configuration: marketsComponentSettings,
                },
            },
        });
        resolve(instance);
    });
}

/**
 * Toggle the disabled status of the stylesheet for the specified component bundle
 * @param {*} componentName - component name
 * @param {*} status - disabled status
 */
function setBundleCssDisabled(componentName, status) {
    let href = '';
    const bundleName = `${componentName}-bundle.css`.toLowerCase();
    for (let i = 0; i < document.styleSheets.length; i += 1) {
        if (document.styleSheets[i].href != null) {
            href = document.styleSheets[i].href.split('/');
            href = href[href.length - 1];
            if (href.toLowerCase() === bundleName) {
                document.styleSheets[i].disabled = status;
                break;
            }
        }
    }
}

/**
 * Create and return an instance of the component
 * @param {*} componentTagName
 * @param {*} componentId
 * @param {*} containerId
 * @param {*} containerParent
 */
function createComponentInstance(componentTagName, componentId, containerId, containerParent) {
    let instance;
    if (window.morningstar.asterix) {
        window.morningstar.asterix.config.setOption('dynamic', true);
        const container = createAndReturnTag(componentTagName, componentId, containerId);
        registerAndResetAsterix(componentId);
        // Bootstrap the element
        angular.bootstrap(container, window.morningstar.asterix.ng.getAngularModuleDependencies());
        containerParent.appendChild(container);
        instance = window.morningstar.asterix.instanceRegistry.find(componentId);
    } else { // Assume MWC
        instance = document.createElement(componentTagName);
        instance.setAttribute('mwc-id', componentId);
        containerParent.appendChild(instance);
    }
    return instance;
}

/**
 * Lazy load a component and add to the specified parent container
 * @param {*} containerParent
 * @param {*} containerId
 * @param {*} componentTagName
 * @param {*} componentId
 */
function lazyLoad(containerParent, containerId, componentTagName, componentId) {
    const initMarkets = componentTagName.indexOf('markets-components-') === 0 && savedLoaderConfig.lazy.initializeMarketsComponent;
    let promiseChain;

    if (!containerParent || !containerId) {
        if (lazyBundleDownloading) {
            return new Promise((resolve) => {
                const clonedLoaderConfig = merge({}, savedLoaderConfig);
                clonedLoaderConfig.lazy.bundlesToDownload = {};
                clonedLoaderConfig.lazy.bundlesToDownload[componentTagName] = true;
                bundlesReady(clonedLoaderConfig, componentLoader.getLoadedFiles()).then(() => {
                    resolve();
                });
            });
        }
        return Promise.resolve();
    }

    if (savedLoaderConfig.css.toggleCssWithComponent && componentTagName) {
        setBundleCssDisabled(componentTagName, false);
    }

    // Lazy bundle downloading
    if (lazyBundleDownloading) {
        promiseChain = new Promise((resolve) => {
            const clonedLoaderConfig = merge({}, savedLoaderConfig);
            clonedLoaderConfig.lazy.bundlesToDownload = {};
            clonedLoaderConfig.lazy.bundlesToDownload[componentTagName] = true;
            bundlesReady(clonedLoaderConfig, componentLoader.getLoadedFiles()).then(() => {
                resolve(createComponentInstance(componentTagName, componentId, containerId, containerParent));
            });
        });
        if (initMarkets) {
            promiseChain.then(() => loadMarketsComponent(containerParent, containerId, componentTagName, componentId));
        }
        return promiseChain;
    }

    // Lazy loading
    if (componentTagName.indexOf('ec-') === 0) {
        return new Promise((resolve) => {
            const path = window.morningstar.asterix.instanceRegistry.find(componentId).modulePath;
            window.morningstar.asterix.instanceRegistry.init(path);
            const container = createAndReturnTag(componentTagName, componentId, containerId);
            // Bootstrap the element
            angular.bootstrap(container, window.morningstar.asterix.ng.getAngularModuleDependencies());
            containerParent.appendChild(container);
            resolve(window.morningstar.asterix.instanceRegistry.find(componentId));
        });
    } else if (initMarkets) {
        return loadMarketsComponent(containerParent, containerId, componentTagName, componentId);
    } else if (!(componentTagName.indexOf('markets-') === 0 || componentTagName.indexOf('sal-') === 0)) {
        return new Promise((resolve) => {
            const instance = document.createElement(componentTagName);
            instance.setAttribute('mwc-id', componentId);
            containerParent.appendChild(instance);
            resolve(instance);
        });
    }
    return promiseChain;
}

/**
 * Remove the specified container and disable CSS for the main element bundle in the container if required
 * @param {*} containerId
 */
function lazyRemove(containerId) {
    const container = document.getElementById(containerId);
    if (container && container.remove) {
        container.remove();
        console.log(`Removed container with id: ${containerId}`);
    }
    // Because IE
    if (container && container.parentNode) {
        container.parentNode.removeChild(container);
        console.log(`Removed container with id: ${containerId} (IE style)`);
    }

    if (savedLoaderConfig.css.toggleCssWithComponent && container && container.firstChild) {
        setBundleCssDisabled(container.firstChild.nodeName, true);
    }
}

/**
 * Get the current runtime environment
 */
function getRuntimeEnvironment() {
    return contextInfo.runtime.environment;
}

/**
 * Get the current runtime region
 */
function getRuntimeRegion() {
    return contextInfo.runtime.region;
}

/**
 * Get the specified API token
 */
function getApiToken(tokenType) {
    if (tokenType.indexOf('Token') >= 0) {
        return contextInfo.apiTokens[tokenType];
    }
    return contextInfo.apiTokens[`${tokenType}Token`];
}

/**
 * Get the current institution id
 */
function getInstId() {
    return contextInfo.instid;
}

/**
 * Get the apiTokenExpiredCallback
 */
function getApiTokenExpiredCallback() {
    return contextInfo.apiTokenExpiredCallback;
}

export default {
    context: {
        getRuntimeEnvironment,
        getRuntimeRegion,
        getApiToken,
        getInstId,
        getApiTokenExpiredCallback,
    },
    lazyLoad,
    lazyRemove,
    load,
    reset,
    version,
};



// WEBPACK FOOTER //
// ./src/loader.js